/**
 * useSessionState — expõe estado corrente sincronizado pelo adapter.
 *
 * Re-renderiza em slideChange / activityChange / interactionModeChange /
 * sessionSnapshot. Guarda também a role autoritativa vinda do backend
 * (via snapshot) — pode divergir da role da URL quando o JWT não bate
 * com master_user_id. Use actualRole para decidir visualização real.
 */

import { useEffect, useState } from 'react'

import { useAdapter } from '../adapter'
import type { InteractionMode } from '../types/adapter'
import type { Role } from '../types/session'

export interface SessionState {
  slideIndex: number
  activityId: string | null
  interactionMode: InteractionMode
  /** Role autoritativa do backend (vem no sessionSnapshot). null antes do primeiro snapshot. */
  actualRole: Role | null
  /** ID de membership no backend (precisa pra scoreboard etc). */
  membershipId: string | null
  /** true após primeiro snapshot chegar. */
  snapshotReceived: boolean
}

export function useSessionState(): SessionState {
  const adapter = useAdapter()
  const [state, setState] = useState<SessionState>({
    slideIndex: adapter.getCurrentSlideIndex(),
    activityId: adapter.getCurrentActivityId(),
    interactionMode: adapter.getInteractionMode(),
    actualRole: null,
    membershipId: null,
    snapshotReceived: false,
  })

  useEffect(() => {
    const offSlide = adapter.on('slideChange', (p: { index: number }) => {
      setState((s) => ({ ...s, slideIndex: p.index }))
    })
    const offActivity = adapter.on(
      'activityChange',
      (p: { activityId: string | null }) => {
        setState((s) => ({ ...s, activityId: p.activityId }))
      },
    )
    const offMode = adapter.on(
      'interactionModeChange',
      (p: { mode: InteractionMode }) => {
        setState((s) => ({ ...s, interactionMode: p.mode }))
      },
    )
    const offSnap = adapter.on(
      'sessionSnapshot',
      (snap: { role: Role; membershipId: string }) => {
        setState((s) => ({
          ...s,
          slideIndex: adapter.getCurrentSlideIndex(),
          activityId: adapter.getCurrentActivityId(),
          interactionMode: adapter.getInteractionMode(),
          actualRole: snap.role,
          membershipId: snap.membershipId,
          snapshotReceived: true,
        }))
      },
    )
    return () => {
      offSlide()
      offActivity()
      offMode()
      offSnap()
    }
  }, [adapter])

  return state
}
